import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton, Stack, Tooltip } from '@mui/material';

type Props = {
  /** Show the edit icon only when the field has a selected value. */
  hasValue?: boolean;
  onCreate?: () => void;
  onEdit?: () => void;
  /** Tooltip + aria-label for the create button. Defaults to "Create new". */
  createTooltip?: string;
  /** Tooltip + aria-label for the edit button. Defaults to "Edit selected". */
  editTooltip?: string;
  /** Disables both buttons. Useful when the parent field is disabled. */
  disabled?: boolean;
  /** Override `disabled` for the create button only (e.g. permission gating). */
  createDisabled?: boolean;
  /** Override `disabled` for the edit button only (e.g. permission gating). */
  editDisabled?: boolean;
};

/**
 * Inline create/edit icon buttons for an Autocomplete-like picker.
 * Designed to be placed inside an `InputAdornment` at the end of the field —
 * see `RHFAutocomplete` for the canonical integration, and the Contact
 * picker in `OrderAccountContactForm` for raw Autocomplete usage.
 *
 * Edit appears only when `hasValue` is `true` so users can't click it on an
 * empty field. Create appears whenever `onCreate` is provided.
 */
export function AutocompleteActions({
  hasValue = false,
  onCreate,
  onEdit,
  createTooltip = 'Create new',
  editTooltip = 'Edit selected',
  disabled = false,
  createDisabled,
  editDisabled,
}: Props) {
  if (!onCreate && !onEdit) return null;
  const showEdit = !!onEdit && hasValue;
  const showCreate = !!onCreate;
  if (!showEdit && !showCreate) return null;

  const isEditDisabled = editDisabled ?? disabled;
  const isCreateDisabled = createDisabled ?? disabled;

  return (
    <Stack alignItems="center" direction="row" spacing={0.5}>
      {showEdit && (
        <Tooltip title={editTooltip}>
          <span>
            <IconButton
              aria-label={editTooltip}
              disabled={isEditDisabled}
              edge="end"
              size="small"
              tabIndex={-1}
              onClick={onEdit}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {showCreate && (
        <Tooltip title={createTooltip}>
          <span>
            <IconButton
              aria-label={createTooltip}
              disabled={isCreateDisabled}
              edge="end"
              size="small"
              tabIndex={-1}
              onClick={onCreate}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Stack>
  );
}
